import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInventory } from '../../contexts/InventoryContext';
import AddSalesListingModal from './AddSalesListingModal';
import SafeIcon from '../../common/SafeIcon';
import JsBarcode from 'jsbarcode';
import * as FiIcons from 'react-icons/fi';

const { 
  FiPlus, FiExternalLink, FiEdit3, FiTrash2, FiShoppingBag, 
  FiInfo, FiTag, FiLock, FiCheckCircle, FiXCircle, FiPrinter, FiImage
} = FiIcons;

const SalesListings = ({ productId }) => {
  const { getProductById, updateSalesListing, deleteSalesListing, getLockedQuantity } = useInventory();
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingListing, setEditingListing] = useState(null);
  
  // Image Modal State
  const [previewImage, setPreviewImage] = useState(null);

  const product = getProductById(productId);
  const salesListings = product?.salesListings || [];
  const lockedCount = getLockedQuantity(product);
  const availableCount = (product?.quantity || 0) - lockedCount;

  const platformColors = {
    'eBay': 'bg-yellow-100 text-yellow-800',
    'Facebook Marketplace': 'bg-blue-100 text-blue-800',
    'OfferUp': 'bg-green-100 text-green-800',
    'Craigslist': 'bg-purple-100 text-purple-800',
    'Amazon': 'bg-orange-100 text-orange-800',
    'Mercari': 'bg-red-100 text-red-800',
    'Other': 'bg-gray-100 text-gray-800'
  };

  const statusConfig = {
    'active': { label: 'Active', color: 'bg-green-100 text-green-800', icon: FiLock },
    'pending_shipping': { label: 'Pending Ship', color: 'bg-orange-100 text-orange-800', icon: FiLock },
    'waiting_for_pickup': { label: 'Wait Pickup', color: 'bg-purple-100 text-purple-800', icon: FiLock },
    'sold': { label: 'Sold', color: 'bg-gray-800 text-white', icon: FiCheckCircle },
    'canceled': { label: 'Canceled', color: 'bg-gray-200 text-gray-600', icon: FiXCircle },
    'paused': { label: 'Paused', color: 'bg-gray-100 text-gray-600', icon: null }
  };

  const handleStatusChange = (listingId, newStatus) => {
    updateSalesListing(productId, listingId, { status: newStatus });
  };

  const handleDelete = (listingId) => {
    if (window.confirm('Are you sure you want to delete this sales listing?')) {
      deleteSalesListing(productId, listingId);
    }
  };

  const getGradeColor = (grade) => {
    if (!grade) return 'bg-gray-100 text-gray-600';
    const g = grade.toLowerCase();
    if (g === 'new') return 'bg-green-100 text-green-800';
    if (g.includes('open box') || g.includes('like new')) return 'bg-blue-100 text-blue-800';
    if (g.includes('parts') || g.includes('repair')) return 'bg-red-100 text-red-800';
    return 'bg-gray-100 text-gray-800';
  };

  const printDymoLabel = (listing) => {
    try {
      const canvas = document.createElement('canvas');
      JsBarcode(canvas, listing.sales_id, {
        format: "CODE128",
        width: 2,
        height: 80,
        displayValue: false,
        margin: 0
      });
      const barcodeDataUrl = canvas.toDataURL("image/png");
      
      const printWindow = window.open('', '', 'width=400,height=300');
      if (!printWindow) {
        alert('Please allow popups to print labels');
        return;
      }

      const html = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>Print Label - ${listing.sales_id}</title>
            <style>
              @page { size: 2.3125in 4in; margin: 0; }
              body { margin: 0; padding: 0.15in; width: 2.3125in; height: 4in; font-family: Arial, sans-serif; display: flex; flex-direction: column; align-items: center; text-align: center; box-sizing: border-box; }
              .header { font-size: 10px; font-weight: bold; text-transform: uppercase; margin-bottom: 5px; width: 100%; border-bottom: 1px solid black; padding-bottom: 2px; }
              .product-name { font-size: 12px; font-weight: bold; margin: 5px 0; line-height: 1.2; max-height: 3.6em; overflow: hidden; width: 100%; word-wrap: break-word; }
              .details { font-size: 10px; margin-bottom: 10px; width: 100%; }
              .barcode-container { margin-top: auto; margin-bottom: 10px; width: 100%; display: flex; flex-direction: column; align-items: center; }
              .barcode-img { max-width: 95%; height: auto; }
              .sales-id-text { font-size: 14px; font-weight: bold; font-family: monospace; margin-top: 2px; }
              .footer { font-size: 9px; margin-top: 5px; }
            </style>
          </head>
          <body>
            <div class="header">Sales Item Label</div>
            <div class="product-name">${product?.name || 'Unknown Product'}</div>
            <div class="details">
              <strong>Loc:</strong> ${listing.platform}<br>
              <strong>Grade:</strong> ${listing.grade}<br>
              ${listing.price ? `<strong>Price:</strong> $${listing.price.toFixed(2)}` : ''}
            </div>
            <div class="barcode-container">
              <img src="${barcodeDataUrl}" class="barcode-img" />
              <div class="sales-id-text">${listing.sales_id}</div>
            </div>
            <div class="footer">${new Date().toLocaleDateString()}</div>
            <script>
              window.onload = function() {
                window.print();
                setTimeout(function() { window.close(); }, 500);
              }
            </script>
          </body>
        </html>
      `;
      printWindow.document.open();
      printWindow.document.write(html);
      printWindow.document.close();
    } catch (e) {
      console.error('Print failed', e);
      alert('Failed to generate print label');
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      <div className="p-6 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center">
            <SafeIcon icon={FiShoppingBag} className="h-5 w-5 text-gray-600 mr-2" />
            <h2 className="text-lg font-semibold text-gray-900">Sales Listings</h2>
            <span className="ml-3 px-2 py-1 bg-gray-100 text-gray-600 text-sm rounded-full">
              {salesListings.length}
            </span>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex flex-col items-end text-xs sm:text-sm">
              <span className="text-gray-500">Inventory Lock: <span className="font-semibold text-gray-800">{lockedCount}</span></span>
              <span className="text-gray-500">Available: <span className={`font-semibold ${availableCount < 1 ? 'text-red-500' : 'text-green-600'}`}>{availableCount}</span></span>
            </div>
            <button
              onClick={() => setShowAddModal(true)}
              disabled={availableCount < 1}
              className="flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              title={availableCount < 1 ? "No inventory available to list" : "Add new listing"}
            >
              <SafeIcon icon={FiPlus} className="h-4 w-4 mr-2" />
              Add Listing
            </button>
          </div>
        </div>
      </div>

      <div className="divide-y divide-gray-200">
        {salesListings.length === 0 ? (
          <div className="p-8 text-center">
            <SafeIcon icon={FiShoppingBag} className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg mb-2">No sales listings yet</p>
            <p className="text-gray-400 mb-4">
              Add listings to track where this product is being sold online
            </p>
            <button
              onClick={() => setShowAddModal(true)}
              disabled={availableCount < 1}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors duration-200 disabled:opacity-50"
            >
              <SafeIcon icon={FiPlus} className="h-4 w-4 mr-2" />
              Add First Listing
            </button>
          </div>
        ) : (
          salesListings.map((listing, index) => {
            const statusInfo = statusConfig[listing.status] || statusConfig['active'];
            return (
              <motion.div
                key={listing.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`p-4 hover:bg-gray-50 transition-colors duration-200 ${listing.status === 'sold' ? 'bg-gray-50 opacity-75' : ''}`}
              >
                <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${platformColors[listing.platform] || platformColors['Other']}`}>
                        {listing.platform}
                      </span>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full flex items-center ${statusInfo.color}`}>
                        {statusInfo.icon && <SafeIcon icon={statusInfo.icon} className="h-3 w-3 mr-1" />}
                        {statusInfo.label}
                      </span>
                      {listing.grade && (
                        <span className={`px-2 py-1 text-xs font-medium rounded-full flex items-center ${getGradeColor(listing.grade)}`}>
                          <SafeIcon icon={FiTag} className="h-3 w-3 mr-1" />
                          {listing.grade}
                        </span>
                      )}
                      {listing.sales_id && (
                        <span className="px-2 py-1 text-xs font-mono font-bold rounded-md bg-gray-100 text-gray-700 border border-gray-300 flex items-center">
                          <SafeIcon icon={FiTag} className="h-3 w-3 mr-1 text-gray-500" />
                          {listing.sales_id}
                        </span>
                      )}
                    </div>
                    
                    <div className="flex items-start gap-4 mb-2">
                      {/* Image Thumbnail */}
                      {listing.images && listing.images.length > 0 && (
                        <div 
                          className="h-16 w-16 flex-shrink-0 bg-gray-100 rounded-lg overflow-hidden border border-gray-200 cursor-pointer hover:opacity-90"
                          onClick={() => setPreviewImage(listing.images[0])}
                        >
                          <img src={listing.images[0]} alt="Listing" className="w-full h-full object-cover" />
                        </div>
                      )}
                      
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-lg font-semibold text-gray-900">
                              ${listing.price.toFixed(2)}
                            </p>
                            {listing.url && (
                              <a
                                href={listing.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center text-sm text-blue-600 hover:text-blue-700 mt-1"
                              >
                                View listing <SafeIcon icon={FiExternalLink} className="h-3 w-3 ml-1" />
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {listing.comments && (
                      <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600 border border-gray-100 mt-2">
                        <div className="flex items-start">
                          <SafeIcon icon={FiInfo} className="h-4 w-4 text-gray-400 mr-2 mt-0.5" />
                          <p>{listing.comments}</p>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col sm:flex-row items-center gap-2 self-start">
                    {/* Quick Status Buttons */}
                    {listing.status === 'active' && (
                      <div className="flex gap-1">
                        <button
                          onClick={() => handleStatusChange(listing.id, 'sold')}
                          className="px-3 py-1.5 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
                        >
                          Mark Sold
                        </button>
                        <button
                          onClick={() => handleStatusChange(listing.id, 'canceled')}
                          className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                    {listing.status === 'canceled' && (
                      <button
                        onClick={() => handleStatusChange(listing.id, 'active')}
                        disabled={availableCount < 1}
                        className="px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-100 hover:bg-blue-200 rounded-lg transition-colors disabled:opacity-50"
                      >
                        Re-List
                      </button>
                    )}
                    
                    <div className="flex gap-2">
                      {listing.sales_id && (
                        <button
                          onClick={() => printDymoLabel(listing)}
                          className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-200"
                          title="Print Dymo Label"
                        >
                          <SafeIcon icon={FiPrinter} className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={() => setEditingListing(listing)}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                        title="Edit Listing Details"
                      >
                        <SafeIcon icon={FiEdit3} className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(listing.id)}
                        className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                        title="Delete Listing"
                      >
                        <SafeIcon icon={FiTrash2} className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })
        )}
      </div>

      {/* Add Sales Listing Modal */}
      {showAddModal && (
        <AddSalesListingModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          productId={productId}
        />
      )}

      {/* Edit Sales Listing Modal */}
      {editingListing && (
        <AddSalesListingModal
          isOpen={!!editingListing}
          onClose={() => setEditingListing(null)}
          productId={productId}
          existingListing={editingListing}
        />
      )}

      {/* Simple Image Preview Modal */}
      {previewImage && (
        <div 
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4"
          onClick={() => setPreviewImage(null)}
        >
          <img 
            src={previewImage} 
            alt="Full Preview" 
            className="max-w-full max-h-full rounded-lg shadow-2xl" 
          />
          <button 
            className="absolute top-4 right-4 text-white hover:text-gray-300"
            onClick={() => setPreviewImage(null)}
          >
            <SafeIcon icon={FiXCircle} className="h-8 w-8" />
          </button>
        </div>
      )}
    </div>
  );
};

export default SalesListings;